import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  EventNote as EventNoteIcon,
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Undo as UndoIcon,
  HourglassEmpty as PendingIcon,
  WarningAmber as WarningIcon,
  EventBusy as EventBusyIcon,
  CalendarToday as CalendarIcon,
  DateRange as DateRangeIcon,
  FilterAltOff as FilterOffIcon,
} from "@mui/icons-material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import {
  fetchTeacherLeavesSelf,
  teacherLeaveWithdraw,
} from "./api/teacherHrApi";
import { endOfDayISO, startOfDayISO } from "./helpers";
import "./teacherHr.css";

/* -------------------- constants -------------------- */

const STATUS_TABS = [
  { key: "All", label: "All" },
  { key: "Pending", label: "Pending" },
  { key: "Approved", label: "Approved" },
  { key: "Rejected", label: "Rejected" },
  { key: "Withdrawn", label: "Withdrawn" },
];

const STATUS_MAP = {
  pending: {
    color: "warning",
    hex: "#f59e0b",
    icon: <PendingIcon fontSize="small" />,
    label: "Pending",
  },
  approved: {
    color: "success",
    hex: "#10b981",
    icon: <CheckCircleIcon fontSize="small" />,
    label: "Approved",
  },
  rejected: {
    color: "error",
    hex: "#ef4444",
    icon: <CancelIcon fontSize="small" />,
    label: "Rejected",
  },
  withdrawn: {
    color: "default",
    hex: "#64748b",
    icon: <UndoIcon fontSize="small" />,
    label: "Withdrawn",
  },
};

/* -------------------- helpers -------------------- */

function normalizeStatusKey(status) {
  return String(status || "")
    .trim()
    .toLowerCase();
}

function statusConfig(status) {
  const key = normalizeStatusKey(status);
  return (
    STATUS_MAP[key] || {
      color: "default",
      hex: "#94a3b8",
      icon: <AccessTimeIcon fontSize="small" />,
      label: status || "Unknown",
    }
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function formatDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return "—";
  }
}

function formatDateFull(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function daysBetween(start, end) {
  if (!start || !end) return 0;
  const diff =
    Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) + 1;
  return diff > 0 ? diff : 0;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return startOfDay(d);
}

/* Inclusive day count between two dates */
function inclusiveDays(a, b) {
  const A = startOfDay(a).getTime();
  const B = startOfDay(b).getTime();
  return Math.max(1, Math.round((B - A) / (1000 * 60 * 60 * 24)) + 1);
}

/* -------------------- component -------------------- */

export default function LeaveMyRequests() {
  const [status, setStatus] = useState("All");

  /* ✅ Default: last 7 days */
  const [fromD, setFromD] = useState(() => daysAgo(6)); // 6 days ago + today = 7 days
  const [toD, setToD] = useState(() => startOfDay(new Date()));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [withdrawId, setWithdrawId] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        from: startOfDayISO(fromD),
        to: endOfDayISO(toD),
        status: status === "All" ? "" : status,
      };
      const res = await fetchTeacherLeavesSelf(params);
      const data = res?.data?.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to load leaves",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, fromD, toD]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmWithdraw = async () => {
    if (!withdrawId) return;
    setWithdrawing(true);
    try {
      await teacherLeaveWithdraw(withdrawId);
      toast.success("Leave withdrawn successfully");
      setWithdrawId(null);
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Withdraw failed",
      );
    } finally {
      setWithdrawing(false);
    }
  };

  const resetFilters = () => {
    setStatus("All");
    setFromD(daysAgo(6));
    setToD(startOfDay(new Date()));
  };

  const stats = useMemo(() => {
    if (!rows.length) return null;
    return {
      total: rows.length,
      pending: rows.filter((r) => normalizeStatusKey(r.status) === "pending")
        .length,
      approved: rows.filter((r) => normalizeStatusKey(r.status) === "approved")
        .length,
      rejected: rows.filter((r) => normalizeStatusKey(r.status) === "rejected")
        .length,
      withdrawn: rows.filter(
        (r) => normalizeStatusKey(r.status) === "withdrawn",
      ).length,
    };
  }, [rows]);

  const rangeDays = inclusiveDays(fromD, toD);
  const rangeValid = startOfDay(fromD).getTime() <= startOfDay(toD).getTime();

  return (
    <Box
      className="orange-scrollBar"
      sx={{
        maxHeight: 600,
        overflow: "auto",
        p: { xs: 1.5, sm: 2 },
        bgcolor: "#f8fafc",
      }}
    >
      {/* -------------------- Header -------------------- */}
      <Box className="mb-3">
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(25,118,210,0.25)",
            }}
          >
            <EventNoteIcon />
          </Box>
          <Box>
            <Typography
              variant="h6"
              className="fw-bold"
              sx={{ lineHeight: 1.2 }}
            >
              My Leave Requests
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track, filter, and withdraw your leave applications
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* -------------------- Summary Cards -------------------- */}
      {stats && (
        <Grid container spacing={1.5} className="mb-3">
          {[
            {
              label: "Total",
              value: stats.total,
              color: "#1976d2",
              bg: "#e3f2fd",
              icon: <CalendarIcon fontSize="small" />,
            },
            {
              label: "Pending",
              value: stats.pending,
              color: "#f59e0b",
              bg: "#fef3c7",
              icon: <PendingIcon fontSize="small" />,
            },
            {
              label: "Approved",
              value: stats.approved,
              color: "#10b981",
              bg: "#d1fae5",
              icon: <CheckCircleIcon fontSize="small" />,
            },
            {
              label: "Rejected",
              value: stats.rejected,
              color: "#ef4444",
              bg: "#fee2e2",
              icon: <CancelIcon fontSize="small" />,
            },
            {
              label: "Withdrawn",
              value: stats.withdrawn,
              color: "#64748b",
              bg: "#e2e8f0",
              icon: <UndoIcon fontSize="small" />,
            },
          ].map((s) => (
            <Grid item xs={6} sm={4} md={2.4} lg={2.4} key={s.label}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e5e7eb",
                  transition: "all .2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                    borderColor: s.color,
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 1.5,
                    "&:last-child": { pb: 1.5 },
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      bgcolor: s.bg,
                      color: s.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        fontSize: 11,
                      }}
                    >
                      {s.label}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: s.color,
                        lineHeight: 1.1,
                      }}
                    >
                      {s.value}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* -------------------- Main Card -------------------- */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #eef2f7",
            bgcolor: "#ffffff",
          }}
        >
          {/* Row 1: Status pills */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 1 }}
          >
            {STATUS_TABS.map((t) => {
              const active = status === t.key;
              const conf = t.key === "All" ? null : statusConfig(t.key);
              return (
                <Button
                  key={t.key}
                  size="small"
                  onClick={() => setStatus(t.key)}
                  disableRipple
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 999,
                    px: 1.75,
                    py: 0.5,
                    minWidth: "auto",
                    fontSize: 13,
                    border: "1px solid",
                    borderColor: active
                      ? conf?.hex || "primary.main"
                      : "#e5e7eb",
                    color: active ? "#fff" : conf?.hex || "text.primary",
                    bgcolor: active
                      ? conf?.hex || "primary.main"
                      : "transparent",
                    "&:hover": {
                      bgcolor: active
                        ? conf?.hex || "primary.main"
                        : conf?.hex
                          ? `${conf.hex}15`
                          : "#f1f5f9",
                      borderColor: conf?.hex || "primary.main",
                    },
                  }}
                >
                  {t.label}
                  {stats && t.key !== "All" && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.75,
                        px: 0.75,
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: active ? "rgba(255,255,255,0.25)" : "#f1f5f9",
                        color: active ? "#fff" : "text.secondary",
                      }}
                    >
                      {stats[normalizeStatusKey(t.key)] ?? 0}
                    </Box>
                  )}
                </Button>
              );
            })}
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {/* Row 2: Date range + actions */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flexWrap: "wrap", rowGap: 1 }}
            >
              <DateRangeIcon
                fontSize="small"
                sx={{ color: "text.secondary" }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  mr: 0.5,
                }}
              >
                Range
              </Typography>

              <DatePicker
                selected={fromD}
                onChange={(d) => d && setFromD(startOfDay(d))}
                selectsStart
                startDate={fromD}
                endDate={toD}
                maxDate={toD}
                dateFormat="dd MMM yyyy"
                className="form-control form-control-sm"
                wrapperClassName="d-inline-block"
                customInput={
                  <Box
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 1.5,
                      px: 1.25,
                      py: 0.5,
                      fontSize: 13,
                      fontWeight: 600,
                      bgcolor: "#fff",
                      minWidth: 130,
                      textAlign: "center",
                      cursor: "pointer",
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    {formatDateShort(fromD)}
                  </Box>
                }
              />

              <Typography variant="body2" color="text.secondary">
                →
              </Typography>

              <DatePicker
                selected={toD}
                onChange={(d) => d && setToD(startOfDay(d))}
                selectsEnd
                startDate={fromD}
                endDate={toD}
                minDate={fromD}
                dateFormat="dd MMM yyyy"
                className="form-control form-control-sm"
                wrapperClassName="d-inline-block"
                customInput={
                  <Box
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 1.5,
                      px: 1.25,
                      py: 0.5,
                      fontSize: 13,
                      fontWeight: 600,
                      bgcolor: "#fff",
                      minWidth: 130,
                      textAlign: "center",
                      cursor: "pointer",
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    {formatDateShort(toD)}
                  </Box>
                }
              />

              <Chip
                size="small"
                label={`${rangeDays} day${rangeDays > 1 ? "s" : ""}`}
                sx={{
                  bgcolor: "#eef2ff",
                  color: "#4f46e5",
                  fontWeight: 700,
                  fontSize: 11,
                  height: 22,
                }}
              />
            </Stack>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1}>
              <Tooltip title="Reset filters">
                <span>
                  <IconButton
                    size="small"
                    onClick={resetFilters}
                    disabled={loading}
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 1.5,
                      "&:hover": { bgcolor: "#f8fafc" },
                    }}
                  >
                    <FilterOffIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={loading ? null : <RefreshIcon fontSize="small" />}
                onClick={load}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  px: 2,
                  boxShadow: "none",
                }}
              >
                {loading ? "Loading…" : "Refresh"}
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Table */}
        <TableContainer
          className="orange-scrollBar"
          sx={{ maxHeight: 340, overflow: "auto", width: "100%" }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{ width: "100%", minWidth: 900 }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    bgcolor: "#f8fafc",
                    color: "#475569",
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    borderBottom: "1px solid #e5e7eb",
                    py: 1.25,
                  },
                }}
              >
                <TableCell sx={{ minWidth: 120 }}>Type</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Start</TableCell>
                <TableCell sx={{ minWidth: 150 }}>End</TableCell>
                <TableCell sx={{ minWidth: 90 }}>Days</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Status</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Reason</TableCell>
                <TableCell align="right" sx={{ minWidth: 120 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton width="80%" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : !rangeValid ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <WarningIcon
                      sx={{ fontSize: 44, color: "warning.main", mb: 1 }}
                    />
                    <Typography variant="body1" fontWeight={600}>
                      Invalid date range
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      “From” date must be earlier than or equal to “To” date.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 7 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        bgcolor: "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 1.5,
                      }}
                    >
                      <EventBusyIcon
                        sx={{ fontSize: 34, color: "action.disabled" }}
                      />
                    </Box>
                    <Typography variant="body1" fontWeight={600}>
                      No leave requests in this range
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1.5 }}
                    >
                      Try widening the dates or changing the status filter.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={resetFilters}
                      sx={{ textTransform: "none", borderRadius: 1.5 }}
                    >
                      Reset filters
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => {
                  const s = statusConfig(row.status);
                  const days = daysBetween(row.startDate, row.endDate);
                  const isPending =
                    normalizeStatusKey(row.status) === "pending";
                  return (
                    <TableRow
                      key={row._id || idx}
                      hover
                      sx={{
                        bgcolor: idx % 2 === 0 ? "#fff" : "#fafbfc",
                        "&:hover": { bgcolor: "#f1f5f9" },
                        "& .row-action": {
                          opacity: { xs: 1, md: 0 },
                          transition: "opacity .15s ease",
                        },
                        "&:hover .row-action, &:focus-within .row-action": {
                          opacity: 1,
                        },
                        "& td": {
                          borderBottom: "1px solid #eef2f7",
                          py: 1.25,
                          verticalAlign: "middle",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 6,
                              height: 24,
                              borderRadius: 1,
                              bgcolor: s.hex,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontSize: 13 }}
                          >
                            {row.leaveType || "—"}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontSize: 13 }}
                        >
                          {formatDate(row.startDate)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: 11 }}
                        >
                          {formatDateFull(row.startDate)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontSize: 13 }}
                        >
                          {formatDate(row.endDate)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: 11 }}
                        >
                          {formatDateFull(row.endDate)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={`${days}d`}
                          sx={{
                            bgcolor: "#f1f5f9",
                            color: "#334155",
                            fontWeight: 700,
                            fontSize: 12,
                            height: 24,
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          icon={s.icon}
                          label={s.label}
                          sx={{
                            bgcolor: `${s.hex}15`,
                            color: s.hex,
                            fontWeight: 700,
                            fontSize: 12,
                            height: 26,
                            border: `1px solid ${s.hex}30`,
                            "& .MuiChip-icon": { color: s.hex, ml: 0.5 },
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Tooltip title={row.reason || ""}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: 13,
                              maxWidth: 260,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.reason || "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="right">
                        {isPending ? (
                          <Button
                            className="row-action"
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<UndoIcon fontSize="small" />}
                            onClick={() => setWithdrawId(row._id)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: 1.5,
                              fontSize: 12,
                            }}
                          >
                            Withdraw
                          </Button>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ fontSize: 11 }}
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* -------------------- Footer: range summary -------------------- */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: "#f8fafc",
            borderTop: "1px solid #eef2f7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <DateRangeIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              Showing data for{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formatDate(fromD)}
              </Box>{" "}
              to{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formatDate(toD)}
              </Box>{" "}
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 999,
                  bgcolor: "#eef2ff",
                  color: "#4f46e5",
                  fontWeight: 700,
                  fontSize: 11,
                }}
              >
                {rangeDays} day{rangeDays > 1 ? "s" : ""}
              </Box>
            </Typography>
          </Stack>

          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {loading
              ? "Loading…"
              : `${rows.length} record${rows.length !== 1 ? "s" : ""}`}
          </Typography>
        </Box>
      </Card>

      {/* -------------------- Withdraw Dialog -------------------- */}
      <Dialog
        open={!!withdrawId}
        onClose={() => !withdrawing && setWithdrawId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            fontWeight: 700,
            pt: 2.5,
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              bgcolor: "#fef3c7",
              color: "#b45309",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WarningIcon fontSize="small" />
          </Box>
          Withdraw Leave?
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This action <strong>cannot be undone</strong>. The request will be
            marked as <strong>Withdrawn</strong> and removed from the approval
            queue.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setWithdrawId(null)}
            disabled={withdrawing}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmWithdraw}
            disabled={withdrawing}
            variant="contained"
            color="warning"
            startIcon={<UndoIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              boxShadow: "none",
            }}
          >
            {withdrawing ? "Withdrawing…" : "Confirm Withdraw"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
